import React, { useEffect } from 'react';
import { get, isEmpty } from 'lodash';
import { Button, Base, appUtils } from 'src/components';
import commonDateUtils from 'common/commonDateUtils';
import { Flyout } from 'src/componentsTailwind/index';
import COMMON_CONSTANTS from 'common/commonConstants';
import { useTree } from 'src/queries/tree';
import CircledIcon from 'src/components/CircledIcon/CircledIcon';
import ReviewOutlinedSVG from 'src/assets/svg/review-outlined.svg';
import { useForm } from 'react-hook-form';
import {
  useGoal,
  useGoalUpdate,
  QUERY_KEYS as GOAL_QUERY_KEYS
} from 'src/queries/goal';
import { toast, Select } from 'src/components/';
import commonGoalUtils from 'common/commonGoalUtils';
import { getGoalStatuses } from 'src/containers/UserProfile/Goals/constants';
import { queryClient } from 'src/index';
import commonViewPermissions from 'common/commonViewPermissions';

const { GOALS } = COMMON_CONSTANTS;

const GoalUpdateFlyout = (props = {}) => {
  const { update = {}, goalId = null, setIsOpen = () => {} } = props;

  const {
    data: { tree },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree();

  const {
    data: goal,
    isFetching: isFetchingGoal,
    isRefetching: isRefetchingGoal,
    isError: isErrorGoal
  } = useGoal(goalId);

  const { mutateAsync: updateGoal, isLoading: isLoadingGoalUpdate } = useGoalUpdate(goalId);

  const isInitialFetchingGoal = isFetchingGoal && !isRefetchingGoal;
  const isFetching = isInitialFetchingGoal || isFetchingTree;
  const isError = isErrorGoal || isErrorTree;
  const isReady = !isFetching && !isError;

  const statusOptions = getGoalStatuses();

  const { watch, reset, setValue } = useForm({
    defaultValues: {
      text: '',
      progress: {},
      status: {}
    }
  });

  const formValues = watch();
  const { text, progress, status } = formValues;
  const resetForm = () => {
    const defaultValues = {
      text: update.text,
      progress: commonGoalUtils.getGoalProgressOption({
        ...goal,
        lastUpdate: update
      }),
      status: commonGoalUtils.getGoalStatusOptionFromLatestUpdate(
        update,
        statusOptions
      )
    };

    reset(defaultValues);
  };

  useEffect(() => {
    if (!isEmpty(goal) && !isEmpty(update)) resetForm();
  }, [goal, update]);

  if (!isReady) {
    return null;
  }

  const isBinary = goal.type === GOALS.TYPES.BINARY;
  const isPercent = goal.type === GOALS.TYPES.PERCENT;

  const loggedUserId = appUtils.getLoggedUserId();
  const canManageGoal = commonViewPermissions.canManageGoal(
    tree,
    loggedUserId,
    goal.assignee
  );

  const saveUpdate = async () => {
    try {
      toast.show('Saving...');

      const payload = [
        {
          id: update.id ?? update._id,
          _id: update.id ?? update._id,
          text,
          author: loggedUserId,
          timestamp: commonDateUtils.getUnixDateNow(),
          progress: Number(get(progress, 'value', progress)),
          status: status.value
        }
      ];

      const response = await updateGoal({ updates: payload });

      if (!response || !response.success) {
        return toast.error(
          response.error ?? 'Failed to save update. Try again later!'
        );
      }

      if (response.warning) {
        const message = get(
          response,
          'message',
          'Failed to save update. Try again later!'
        );
        toast.closeAll();
        return toast.show(message);
      }

      toast.show('Update changed!');

      await queryClient.refetchQueries([GOAL_QUERY_KEYS.GOALS, goal.id]);
      await queryClient.invalidateQueries(GOAL_QUERY_KEYS.GOALS_LOOKUP);

      setIsOpen(false);
    } catch (error) {
      return toast.error(error ?? 'Try again!');
    }
  };

  const deleteUpdate = async () => {
    try {
      toast.show('Deleting update...');
      const payload = [update.id ?? update._id];
      const response = await updateGoal({ deleteUpdates: payload });

      if (!response || !response.success) {
        return toast.error(response.error ?? 'Try again!');
      }

      if (response.warning) {
        const message = get(
          response,
          'message',
          'Failed to delete goal. Try again later!'
        );
        toast.closeAll();
        return toast.show(message);
      }

      toast.show('Update deleted!');

      queryClient.invalidateQueries([GOAL_QUERY_KEYS.GOALS, goalId]);
      queryClient.invalidateQueries(GOAL_QUERY_KEYS.GOALS_LOOKUP);

      setIsOpen(false);
      return true;
    } catch (error) {
      return toast.error(error ?? 'Try again!');
    }
  };

  const renderProgressField = () => {
    if (isBinary) {
      const options = [
        { value: 0, label: 'Not done' },
        { value: 1, label: 'Done' }
      ];

      return (
        <Select
          classes='w-full'
          optionsWidth='fit'
          maxOptionWidth='500px'
          placeholder='Any progress?'
          options={options}
          title={get(progress, 'label')}
          disabled={!canManageGoal || isLoadingGoalUpdate}
          onChange={(option) => setValue('progress', option)}
        />
      );
    }

    return (
      <input
        className='p-2 border border-mid-gray bg-white rounded-lg resize-none w-full'
        placeholder='Any progress?'
        type='number'
        min={isPercent ? 0 : undefined}
        max={isPercent ? 100 : undefined}
        value={progress}
        disabled={!canManageGoal || isLoadingGoalUpdate}
        onChange={(e) => setValue('progress', e.target.value)}
      />
    );
  };

  const getHasDataChanged = () => {
    if (text !== update.text) {
      return true;
    }

    if (get(status, 'value') !== update.status) {
      return true;
    }

    if (isBinary) {
      return Number(get(progress, 'value')) !== Number(update.progress);
    }

    return Number(progress) !== Number(update.progress);
  };

  const getIsDataValid = () => {
    if (isEmpty(text) || text.length < 10) {
      return false;
    }

    if (isEmpty(status.value)) {
      return false;
    }

    if (isBinary) {
      if (!isFinite(progress.value)) {
        return false;
      }
    } else if (!isFinite(progress)) {
      return false;
    }

    if (!getHasDataChanged()) {
      return false;
    }

    if (isPercent && (progress < 0 || progress > 100)) {
      return false;
    }

    return true;
  };

  return (
    <Flyout {...props} lock={isLoadingGoalUpdate}>
      <Base classes='h-[100%] w-full' relative>
        <CircledIcon svg={<ReviewOutlinedSVG className='text-purple' />} />
        <h2 className='text-xl font-bold h-[3%] mr-8'>Goal Update Details</h2>
        <p className='text-sm font-normal mt-2 mb-4 leading-5 text-bluish-gray h-[3%]'>
          Information related to this goal update.
        </p>
        <div className='border-b border-mid-gray my-3 transform -translate-x-[32px] w-[calc(100%+64px)]' />
        <div className='flex flex-col gap-2'>
          <div className='flex flex-col gap-1'>
            <p className='m-0'>Update</p>
            <textarea
              className='w-full p-2 border border-mid-gray bg-white rounded-lg resize-none h-24'
              value={text}
              placeholder='Add an update'
              disabled={!canManageGoal || isLoadingGoalUpdate}
              onChange={(e) => setValue('text', e.target.value)}
            />
          </div>
          <div className='w-full flex gap-1'>
            <div className='flex flex-col gap-1 w-3/5'>
              <p className='m-0'>Progress</p>
              {renderProgressField()}
            </div>
            <div className='flex flex-col gap-1 w-2/5'>
              <p className='m-0'>Status</p>
              <Select
                classes='w-full'
                optionsWidth='fit'
                maxOptionWidth='500px'
                options={statusOptions}
                title={get(status, 'label')}
                disabled={!canManageGoal || isLoadingGoalUpdate}
                onChange={(option) => setValue('status', option)}
              />
            </div>
          </div>
          {canManageGoal ? (
            <div className='w-full flex justify-end gap-2'>
              <Button
                disabled={isLoadingGoalUpdate}
                variant='black-outline'
                onClick={deleteUpdate}
              >
                Delete update
              </Button>
              <Button
                disabled={isLoadingGoalUpdate || !getIsDataValid()}
                variant='black'
                onClick={saveUpdate}
              >
                Edit update
              </Button>
            </div>
          ) : null}
        </div>
      </Base>
    </Flyout>
  );
};

export default GoalUpdateFlyout;
